import type { Game, MemberPublic, PostEnriched } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';
import { findGameFormat } from '@/lib/reference/golf-games';

interface Props {
  games: Game[];
  posts: PostEnriched[];
  memberMap: Record<string, MemberPublic>;
  courseNames: Record<string, string>;
  onOpenGame?: (id: string) => void;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function gameIsToday(g: Game): boolean {
  const start = startOfToday().getTime();
  const end = endOfToday().getTime();
  const teeTime = typeof g.config?.tee_time === 'string' ? g.config.tee_time : null;
  if (teeTime) {
    const t = new Date(teeTime).getTime();
    if (!Number.isNaN(t) && t >= start && t <= end) return true;
  }
  const scheduled = typeof g.config?.scheduled_date === 'string' ? g.config.scheduled_date : null;
  if (scheduled) {
    const todayStr = startOfToday().toISOString().split('T')[0];
    return scheduled === todayStr;
  }
  const created = new Date(g.created_at).getTime();
  return created >= start && created <= end;
}

function postIsToday(p: PostEnriched): boolean {
  const t = new Date(p.created_at).getTime();
  return t >= startOfToday().getTime() && t <= endOfToday().getTime();
}

export function MorningTeeSheet({ games, posts, memberMap, courseNames, onOpenGame }: Props) {
  const now = new Date();
  const hour = now.getHours();

  const todayGames = games
    .filter((g) => g.status !== 'completed' && g.status !== 'cancelled' && gameIsToday(g))
    .sort((a, b) => {
      const ta = typeof a.config?.tee_time === 'string' ? new Date(a.config.tee_time as string).getTime() : new Date(a.created_at).getTime();
      const tb = typeof b.config?.tee_time === 'string' ? new Date(b.config.tee_time as string).getTime() : new Date(b.created_at).getTime();
      return ta - tb;
    });

  const todayPosts = posts.filter((p) => postIsToday(p) && p.type === 'round');

  const total = todayGames.length + todayPosts.length;
  if (total === 0 && hour >= 14) return null;

  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'This afternoon' : 'Tonight';
  const dayName = now.toLocaleDateString([], { weekday: 'long' });
  const iconBg = hour < 12 ? 'rgba(196,166,98,0.18)' : 'rgba(10,30,63,0.08)';
  const iconColor = hour < 12 ? '#8a6d3d' : '#0a1e3f';

  return (
    <div className="mb-3.5 rounded-2xl border border-line bg-paper p-3.5 shadow-bt">
      <div className={`flex items-center gap-2.5 ${total > 0 ? 'mb-3' : 'mb-1'}`}>
        <div
          className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-xl"
          style={{ background: iconBg, color: iconColor }}
        >
          {hour < 12 ? '☀️' : hour < 17 ? '⛳' : '🌙'}
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-[0.12em] text-mute">
            {greeting.toUpperCase()} · {dayName.toUpperCase()}
          </div>
          <div className="mt-px font-serif text-[15px] font-bold leading-tight text-navy">
            {total === 0 ? 'A quiet day at the club so far' : total === 1 ? 'One group at the club today' : `${total} groups at the club today`}
          </div>
        </div>
      </div>

      {todayGames.length > 0 && (
        <div className="mt-2 space-y-1">
          {todayGames.slice(0, 4).map((g) => {
            const organizer = memberMap[g.organizer_id];
            const format = findGameFormat(g.format_id);
            const formatName = format?.name || g.format_id.charAt(0).toUpperCase() + g.format_id.slice(1);
            const teeRaw = typeof g.config?.tee_time === 'string' ? g.config.tee_time : null;
            const teeStr = teeRaw
              ? new Date(teeRaw).toLocaleString([], { hour: 'numeric', minute: '2-digit' })
              : null;
            const isJoinable = Boolean(g.config?.is_joinable) && !g.track_stakes;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => onOpenGame?.(g.id)}
                className="flex w-full items-center gap-2.5 rounded-[10px] border border-line bg-cream px-2.5 py-2 text-left"
              >
                {organizer ? (
                  <Avatar member={organizer} size={28} />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-gold">?</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-semibold text-ink">
                    {organizer ? organizer.name.split(' ')[0] : 'Member'} · {formatName}
                  </div>
                  <div className="mt-px truncate text-[10px] text-mute">
                    {(courseNames[g.course_id] || 'TBD').replace(/^ANCC /, '')}
                    {teeStr ? ` · ${teeStr}` : ''}
                  </div>
                </div>
                {isJoinable && (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[8px] font-extrabold tracking-wide text-gold-deep" style={{ background: 'rgba(196,166,98,0.18)' }}>
                    OPEN
                  </span>
                )}
              </button>
            );
          })}
          {todayGames.length > 4 && (
            <div className="pt-1 text-center text-[10px] italic text-mute">+ {todayGames.length - 4} more</div>
          )}
        </div>
      )}

      {todayPosts.length > 0 && todayGames.length === 0 && (
        <div className="mt-1 text-[11px] text-ink-soft">
          {todayPosts.slice(0, 3).map((p, i) => {
            const m = memberMap[p.author_id];
            const first = m?.name.split(' ')[0];
            return (
              <span key={p.id}>
                {first}
                {i < Math.min(2, todayPosts.length - 1) ? ', ' : ''}
              </span>
            );
          })}
          {' posted '}
          {todayPosts.length === 1 ? 'a round' : `${todayPosts.length} rounds`} today.
        </div>
      )}
    </div>
  );
}
